import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, AutoFixHigh as AutoFixHighIcon } from '@mui/icons-material';
import VendedorSelect from '../../../components/VendedorSelect';
import {
  CreateVisitasPlanRangePayload,
  UpdateVisitasPlanDto,
  VisitasPlanDto,
} from '../../../types/visitas';
import ClienteSearch from '../../../components/Tesoreria/ClienteSearch';
import DireccionClienteSearch from '../../../components/Tesoreria/DireccionClienteSearch';
import { countIsoDaysInRange, eachIsoDayInRange } from './planFormHelpers';
import {
  buildVisitasPorDiaPayload,
  countDaysWithVisits,
  countRowsInDraftByDay,
  countValidVisitsInDraft,
  DraftByDay,
  formatDayTabLabel,
  makeRowRefKey,
  mergeAgendaIntoDraftByDay,
  stripAgendaRowsFromDraftByDay,
  syncDraftByDayToRange,
} from './planDraftByDay';
import PlanVisitaItemRow from './PlanVisitaItemRow';
import {
  emptyVisitRow,
  handleDireccionSelected,
  noSpinnerSx,
  VisitRowState,
} from './planVisitaFormUi';
import { loadDireccionesForVisitRow, validateVisitRow } from './planVisitaRowLoaders';
import { useSnackbar } from 'notistack';
import { DEFAULT_AGENDA_ID, DEFAULT_AGENDA_NOMBRE } from '../../../services/visitasAgendaStorage';
import { previewAutocompletarPlanes } from '../../../services/visitasAgendaAutocompletar';
import {
  buildDraftByDayFromAgendaCandidatos,
  fetchAgendaCandidatos,
} from '../../../services/visitasAgendaPlanDraft';
import { VisitasAgendaAutocompletarPreview } from '../../../types/visitasAgenda';

interface VendedorOption {
  nroVendedor: number;
  nombreVendedor?: string;
}

interface PlanFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  vendedores: VendedorOption[];
  plan?: VisitasPlanDto | null;
  saving?: boolean;
  onClose: () => void;
  onSubmitCreate: (payload: CreateVisitasPlanRangePayload) => Promise<void>;
  onSubmitEdit: (payload: UpdateVisitasPlanDto) => Promise<void>;
}

const formatDateForInput = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const PlanFormDialog: React.FC<PlanFormDialogProps> = ({
  open,
  mode,
  vendedores,
  plan,
  saving = false,
  onClose,
  onSubmitCreate,
  onSubmitEdit,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [nroVendedor, setNroVendedor] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [estadoPlan, setEstadoPlan] = useState('');
  const [draftByDay, setDraftByDay] = useState<DraftByDay>({});
  const [diaActivo, setDiaActivo] = useState('');
  const loadedClientePorFilaRef = useRef<Map<string, number>>(new Map());
  const [clienteSearchOpen, setClienteSearchOpen] = useState(false);
  const [clientePickerIndex, setClientePickerIndex] = useState<number | null>(null);
  const [clientePickerForComposer, setClientePickerForComposer] = useState(false);
  const [direccionSearchOpen, setDireccionSearchOpen] = useState(false);
  const [direccionPickerIndex, setDireccionPickerIndex] = useState<number | null>(null);
  const [direccionPickerForComposer, setDireccionPickerForComposer] = useState(false);
  const [pendingVisitRow, setPendingVisitRow] = useState<VisitRowState | null>(null);
  const pendingVisitRef = useRef<HTMLDivElement>(null);
  const pendingClienteLoadedRef = useRef<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [usarAgenda, setUsarAgenda] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [agregarVisitasLoading, setAgregarVisitasLoading] = useState(false);
  const [previewData, setPreviewData] = useState<VisitasAgendaAutocompletarPreview | null>(null);

  const isEdit = mode === 'edit';
  const isClosedPlan = isEdit && String(plan?.estadoPlan || '').trim().toLowerCase() === 'cerrado';

  const resetDraft = () => {
    setDraftByDay({});
    setDiaActivo('');
    loadedClientePorFilaRef.current = new Map();
  };

  const rangeDays = useMemo(
    () => (isEdit ? [] : eachIsoDayInRange(fechaDesde, fechaHasta)),
    [isEdit, fechaDesde, fechaHasta],
  );

  useEffect(() => {
    if (!open) return;
    setSubmitError(null);
    setClienteSearchOpen(false);
    setClientePickerIndex(null);
    setClientePickerForComposer(false);
    setDireccionSearchOpen(false);
    setDireccionPickerIndex(null);
    setDireccionPickerForComposer(false);
    setPendingVisitRow(null);
    pendingClienteLoadedRef.current = null;
    if (isEdit && plan) {
      const planFecha = formatDateForInput(plan.fecha);
      setFechaDesde(planFecha);
      setFechaHasta(planFecha);
      setNroVendedor(String(plan.nroVendedor ?? ''));
      setObservaciones(plan.observaciones ?? '');
      setEstadoPlan(plan.estadoPlan ?? '');
      resetDraft();
      return;
    }

    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const today = `${yyyy}-${mm}-${dd}`;
    setFechaDesde(today);
    setFechaHasta(today);
    setNroVendedor('');
    setObservaciones('');
    setEstadoPlan('');
    resetDraft();
    setUsarAgenda(true);
    setPreviewData(null);
  }, [open, isEdit, plan]);

  useEffect(() => {
    if (isEdit || rangeDays.length === 0) return;
    setDraftByDay((prev) => syncDraftByDayToRange(prev, rangeDays));
    setDiaActivo((prev) => (rangeDays.includes(prev) ? prev : rangeDays[0]));
  }, [isEdit, fechaDesde, fechaHasta, rangeDays]);

  useEffect(() => {
    if (isEdit) return;
    setPreviewData(null);
    setDraftByDay((prev) => stripAgendaRowsFromDraftByDay(prev));
    loadedClientePorFilaRef.current = new Map();
  }, [isEdit, nroVendedor, usarAgenda]);

  const rangeDaysCount = useMemo(
    () => (isEdit ? 1 : countIsoDaysInRange(fechaDesde, fechaHasta)),
    [isEdit, fechaDesde, fechaHasta],
  );

  const rangeError = useMemo(() => {
    if (isEdit) return null;
    if (!fechaDesde || !fechaHasta) return null;
    if (fechaDesde > fechaHasta) return 'La fecha hasta debe ser igual o posterior a la fecha desde.';
    if (rangeDaysCount === 0) return 'El rango de fechas no es válido.';
    return null;
  }, [isEdit, fechaDesde, fechaHasta, rangeDaysCount]);

  const canUseAgendaActions = useMemo(
    () => Boolean(fechaDesde && fechaHasta && nroVendedor && !rangeError),
    [fechaDesde, fechaHasta, nroVendedor, rangeError],
  );

  const activeRows = useMemo(
    () => (diaActivo ? draftByDay[diaActivo] ?? [] : []),
    [draftByDay, diaActivo],
  );

  const plansToCreateCount = useMemo(
    () => countDaysWithVisits(draftByDay, rangeDays),
    [draftByDay, rangeDays],
  );

  const canSubmit = useMemo(() => {
    if (isEdit) return true;
    if (!canUseAgendaActions) return false;
    return countValidVisitsInDraft(draftByDay) > 0;
  }, [isEdit, canUseAgendaActions, draftByDay]);

  const updateActiveDayRows = (updater: (rows: VisitRowState[]) => VisitRowState[]) => {
    if (!diaActivo) return;
    setDraftByDay((prev) => ({
      ...prev,
      [diaActivo]: updater(prev[diaActivo] ?? []),
    }));
  };

  const updateVisitRow = (index: number, patch: Partial<VisitRowState>) => {
    updateActiveDayRows((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const loadDireccionesForRow = async (index: number, nroCliente: number, force = false) => {
    if (!diaActivo) return;
    const refKey = makeRowRefKey(diaActivo, index);

    if (!nroCliente || nroCliente <= 0) {
      loadedClientePorFilaRef.current.delete(refKey);
      updateVisitRow(index, {
        direcciones: [],
        codigoDireccion: '',
        direccionLabel: '',
        direccionLoading: false,
      });
      return;
    }

    if (!force && loadedClientePorFilaRef.current.get(refKey) === nroCliente) {
      return;
    }

    loadedClientePorFilaRef.current.set(refKey, nroCliente);
    updateVisitRow(index, {
      direccionLoading: true,
      codigoDireccion: '',
      direccionLabel: '',
      direcciones: [],
    });

    const patch = await loadDireccionesForVisitRow(nroCliente);
    if (loadedClientePorFilaRef.current.get(refKey) !== nroCliente) {
      return;
    }
    if (!patch.direcciones.length && !patch.codigoDireccion) {
      loadedClientePorFilaRef.current.delete(refKey);
    }
    updateVisitRow(index, patch);
  };

  const loadDireccionesForPendingVisit = async (nroCliente: number, force = false) => {
    if (!nroCliente || nroCliente <= 0) {
      pendingClienteLoadedRef.current = null;
      setPendingVisitRow((prev) =>
        prev
          ? {
              ...prev,
              direcciones: [],
              codigoDireccion: '',
              direccionLabel: '',
              direccionLoading: false,
            }
          : null,
      );
      return;
    }

    if (!force && pendingClienteLoadedRef.current === nroCliente) {
      return;
    }

    pendingClienteLoadedRef.current = nroCliente;
    setPendingVisitRow((prev) =>
      prev
        ? {
            ...prev,
            direccionLoading: true,
            codigoDireccion: '',
            direccionLabel: '',
            direcciones: [],
          }
        : null,
    );

    const patch = await loadDireccionesForVisitRow(nroCliente);
    if (pendingClienteLoadedRef.current !== nroCliente) {
      return;
    }
    if (!patch.direcciones.length && !patch.codigoDireccion) {
      pendingClienteLoadedRef.current = null;
    }
    setPendingVisitRow((prev) => (prev ? { ...prev, ...patch } : null));
  };

  const handleStartAddItem = () => {
    if (!diaActivo || pendingVisitRow) return;
    setSubmitError(null);
    setPendingVisitRow(emptyVisitRow());
    pendingClienteLoadedRef.current = null;
  };

  const handleCancelPendingVisit = () => {
    setPendingVisitRow(null);
    pendingClienteLoadedRef.current = null;
    setClientePickerForComposer(false);
    setDireccionPickerForComposer(false);
  };

  const handleConfirmPendingVisit = () => {
    if (!diaActivo || !pendingVisitRow) return;
    setSubmitError(null);
    if (Number(pendingVisitRow.nroCliente) <= 0) {
      setSubmitError('Seleccione un cliente antes de agregar la visita.');
      return;
    }
    const label = `${formatDayTabLabel(diaActivo)} — nueva visita`;
    const validationError = validateVisitRow(pendingVisitRow, label);
    if (validationError) {
      setSubmitError(validationError);
      return;
    }
    updateActiveDayRows((rows) => [...rows, pendingVisitRow]);
    handleCancelPendingVisit();
  };

  const patchPendingVisitRow = (patch: Partial<VisitRowState>) => {
    setPendingVisitRow((prev) => (prev ? { ...prev, ...patch } : null));
  };

  useEffect(() => {
    if (!pendingVisitRow) return;
    const timer = window.setTimeout(() => {
      pendingVisitRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [pendingVisitRow]);

  useEffect(() => {
    setPendingVisitRow(null);
    pendingClienteLoadedRef.current = null;
    setClientePickerForComposer(false);
    setDireccionPickerForComposer(false);
  }, [diaActivo]);

  const handleRemoveItem = (index: number) => {
    if (!diaActivo) return;
    updateActiveDayRows((rows) => rows.filter((_, i) => i !== index));
    loadedClientePorFilaRef.current.delete(makeRowRefKey(diaActivo, index));
    const remapped = new Map<string, number>();
    loadedClientePorFilaRef.current.forEach((nro, key) => {
      const [day, idxStr] = key.split('|');
      const idx = Number(idxStr);
      if (day !== diaActivo) {
        remapped.set(key, nro);
        return;
      }
      if (idx < index) remapped.set(key, nro);
      else if (idx > index) remapped.set(makeRowRefKey(day, idx - 1), nro);
    });
    loadedClientePorFilaRef.current = remapped;
  };

  const validateDraftByDay = (): string | null => {
    for (const day of rangeDays) {
      const rows = draftByDay[day] ?? [];
      for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index];
        if (Number(row.nroCliente) <= 0) continue;
        const label = `${formatDayTabLabel(day)} — visita ${index + 1}`;
        const err = validateVisitRow(row, label);
        if (err) return err;
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    if (isEdit) {
      await onSubmitEdit({
        observaciones,
        estadoPlan: estadoPlan || undefined,
      });
      return;
    }

    const validationError = validateDraftByDay();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    await onSubmitCreate({
      fechaDesde,
      fechaHasta,
      nroVendedor: Number(nroVendedor),
      observaciones: observaciones || null,
      visitasPorDia: buildVisitasPorDiaPayload(draftByDay, rangeDays),
      agendaId: null,
      usarAgendaAutocompletar: false,
    });
  };

  const handleAgregarVisitasDesdeAgenda = async () => {
    setSubmitError(null);
    if (!canUseAgendaActions) {
      setSubmitError('Complete vendedor y rango de fechas válido.');
      return;
    }
    setAgregarVisitasLoading(true);
    try {
      const candidatos = await fetchAgendaCandidatos(Number(nroVendedor));
      const agendaByDay = await buildDraftByDayFromAgendaCandidatos(candidatos, fechaDesde, fechaHasta);
      const totalFilas = countRowsInDraftByDay(agendaByDay);

      setDraftByDay((prev) => mergeAgendaIntoDraftByDay(prev, agendaByDay));
      loadedClientePorFilaRef.current = new Map();

      if (totalFilas === 0) {
        const msg = 'No hay visitas de agenda para el vendedor y rango seleccionados.';
        setSubmitError(msg);
        enqueueSnackbar(msg, { variant: 'warning' });
        return;
      }

      enqueueSnackbar(
        `${totalFilas} visita(s) cargadas en el borrador. Revise por día y pulse «Crear plan».`,
        { variant: 'success' },
      );
    } catch {
      setSubmitError('No se pudieron cargar las visitas desde la agenda.');
      enqueueSnackbar('No se pudieron cargar las visitas desde la agenda.', { variant: 'error' });
    } finally {
      setAgregarVisitasLoading(false);
    }
  };

  const handlePreviewAutocompletar = async () => {
    setSubmitError(null);
    if (!fechaDesde || !fechaHasta || !nroVendedor || rangeError) {
      setSubmitError('Complete vendedor y rango de fechas válido.');
      return;
    }
    setPreviewLoading(true);
    try {
      const data = await previewAutocompletarPlanes(
        DEFAULT_AGENDA_ID,
        Number(nroVendedor),
        fechaDesde,
        fechaHasta,
      );
      setPreviewData(data);
      setPreviewOpen(true);
    } catch {
      setSubmitError('No se pudo calcular la vista previa.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const openClientePicker = (rowIndex: number) => {
    setClientePickerForComposer(false);
    setClientePickerIndex(rowIndex);
    setClienteSearchOpen(true);
  };

  const openPendingClientePicker = () => {
    setClientePickerForComposer(true);
    setClientePickerIndex(null);
    setClienteSearchOpen(true);
  };

  const handleClienteSelected = async (cliente: {
    nroCliente: number;
    nombre?: string;
    razonSocial?: string;
  }) => {
    const clienteNombre =
      [cliente.nombre, cliente.razonSocial].filter(Boolean).join(' — ') ||
      `Cliente ${cliente.nroCliente}`;

    if (clientePickerForComposer) {
      pendingClienteLoadedRef.current = null;
      setPendingVisitRow((prev) => ({
        ...(prev ?? emptyVisitRow()),
        nroCliente: cliente.nroCliente,
        clienteNombre,
        codigoDireccion: '',
        direccionLabel: '',
        direcciones: [],
        direccionLoading: true,
      }));
      setClienteSearchOpen(false);
      setClientePickerForComposer(false);
      await loadDireccionesForPendingVisit(cliente.nroCliente, true);
      return;
    }

    if (clientePickerIndex === null || !diaActivo) return;
    const idx = clientePickerIndex;

    loadedClientePorFilaRef.current.delete(makeRowRefKey(diaActivo, idx));
    updateVisitRow(idx, {
      nroCliente: cliente.nroCliente,
      clienteNombre,
      codigoDireccion: '',
      direccionLabel: '',
      direcciones: [],
      direccionLoading: true,
    });
    setClienteSearchOpen(false);
    setClientePickerIndex(null);
    await loadDireccionesForRow(idx, cliente.nroCliente, true);
  };

  const openDireccionPicker = (rowIndex: number) => {
    setDireccionPickerForComposer(false);
    setDireccionPickerIndex(rowIndex);
    setDireccionSearchOpen(true);
  };

  const openPendingDireccionPicker = () => {
    setDireccionPickerForComposer(true);
    setDireccionPickerIndex(null);
    setDireccionSearchOpen(true);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>{isEdit ? 'Editar Plan de Visitas' : 'Nuevo Plan de Visitas'}</DialogTitle>
        <DialogContent dividers sx={noSpinnerSx}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={isEdit ? 'Fecha' : 'Fecha desde'}
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={isEdit}
                error={Boolean(rangeError)}
              />
            </Grid>
            {!isEdit && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Fecha hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(rangeError)}
                  helperText={
                    rangeError ||
                    (rangeDaysCount > 1
                      ? `Se crearán hasta ${rangeDaysCount} planes (uno por día con visitas).`
                      : 'Un solo día: se crea un plan.')
                  }
                />
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <VendedorSelect
                value={nroVendedor ? Number(nroVendedor) : null}
                onChange={(nro) => setNroVendedor(nro != null ? String(nro) : '')}
                vendedores={vendedores}
                required
                disabled={isEdit}
              />
            </Grid>
            {isEdit && (
              <Grid item xs={12} md={4}>
                {isClosedPlan ? (
                  <TextField
                    fullWidth
                    label="Estado del Plan"
                    value={estadoPlan || 'Cerrado'}
                    InputProps={{ readOnly: true }}
                    helperText="Plan cerrado: el estado está bloqueado."
                  />
                ) : (
                  <TextField
                    fullWidth
                    select
                    label="Estado del Plan"
                    value={estadoPlan}
                    onChange={(e) => setEstadoPlan(e.target.value)}
                  >
                    <MenuItem value="">Sin cambios</MenuItem>
                    <MenuItem value="Abierto">Abierto</MenuItem>
                    <MenuItem value="EnCurso">EnCurso</MenuItem>
                    <MenuItem value="Cerrado">Cerrado</MenuItem>
                  </TextField>
                )}
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Observaciones"
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
              />
            </Grid>
            {!isEdit && (
              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={usarAgenda}
                        onChange={(e) => setUsarAgenda(e.target.checked)}
                      />
                    }
                    label={`Usar ${DEFAULT_AGENDA_NOMBRE}`}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AutoFixHighIcon />}
                    disabled={!usarAgenda || previewLoading || agregarVisitasLoading || !canUseAgendaActions}
                    onClick={handlePreviewAutocompletar}
                  >
                    Vista previa
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={
                      !usarAgenda ||
                      previewLoading ||
                      agregarVisitasLoading ||
                      !canUseAgendaActions
                    }
                    onClick={() => void handleAgregarVisitasDesdeAgenda()}
                  >
                    {agregarVisitasLoading ? 'Agregando…' : 'Agregar visitas'}
                  </Button>
                </Stack>
              </Grid>
            )}
          </Grid>

          {!isEdit && (
            <Box sx={{ mt: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Visitas del plan
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {rangeDaysCount > 1
                      ? 'Seleccione un día. «Agregar visita» abre un formulario antes de sumar al día visible.'
                      : usarAgenda
                        ? 'Use «Agregar visitas» para cargar la agenda, o «Agregar visita» para cargar una manualmente.'
                        : 'Complete cliente y dirección en el formulario antes de agregar cada visita.'}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleStartAddItem}
                  disabled={!diaActivo || Boolean(pendingVisitRow)}
                >
                  Agregar visita
                </Button>
              </Stack>

              {rangeDaysCount > 1 && rangeDays.length > 0 ? (
                <Tabs
                  value={diaActivo}
                  onChange={(_, value: string) => setDiaActivo(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                >
                  {rangeDays.map((day) => {
                    const count = (draftByDay[day] ?? []).filter((row) => Number(row.nroCliente) > 0).length;
                    return (
                      <Tab
                        key={day}
                        value={day}
                        label={formatDayTabLabel(day, count)}
                        sx={{ minHeight: 48, textTransform: 'none' }}
                      />
                    );
                  })}
                </Tabs>
              ) : null}

              {submitError ? (
                <Typography color="error" variant="body2" sx={{ mb: 1.5 }}>
                  {submitError}
                </Typography>
              ) : null}

              <Stack spacing={1.5}>
                {pendingVisitRow ? (
                  <Box
                    ref={pendingVisitRef}
                    sx={{
                      p: 2,
                      border: '2px solid',
                      borderColor: 'primary.main',
                      borderRadius: 2,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                      Nueva visita
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                      Elija cliente y dirección. Horario y observación son opcionales.
                    </Typography>
                    <PlanVisitaItemRow
                      row={pendingVisitRow}
                      onPatch={patchPendingVisitRow}
                      onOpenClientePicker={openPendingClientePicker}
                      onOpenDireccionPicker={openPendingDireccionPicker}
                    />
                    <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1.5 }}>
                      <Button size="small" onClick={handleCancelPendingVisit}>
                        Cancelar
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleConfirmPendingVisit}
                        disabled={Number(pendingVisitRow.nroCliente) <= 0 || pendingVisitRow.direccionLoading}
                      >
                        Agregar a la lista
                      </Button>
                    </Stack>
                  </Box>
                ) : null}
                {activeRows.map((row, index) => (
                  <PlanVisitaItemRow
                    key={`${diaActivo}-${index}`}
                    row={row}
                    onPatch={(patch) => updateVisitRow(index, patch)}
                    onOpenClientePicker={() => openClientePicker(index)}
                    onOpenDireccionPicker={() => openDireccionPicker(index)}
                    onRemove={() => handleRemoveItem(index)}
                  />
                ))}
                {activeRows.length === 0 && diaActivo ? (
                  <Typography variant="body2" color="text.secondary">
                    Sin visitas para {formatDayTabLabel(diaActivo)}.
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || saving || agregarVisitasLoading}>
            {isEdit
              ? 'Guardar cambios'
              : plansToCreateCount > 1
                ? `Crear ${plansToCreateCount} planes`
                : 'Crear plan'}
          </Button>
        </DialogActions>
      </Dialog>

      <ClienteSearch
        open={clienteSearchOpen}
        onClose={() => {
          setClienteSearchOpen(false);
          setClientePickerIndex(null);
          setClientePickerForComposer(false);
        }}
        onSelect={handleClienteSelected}
      />

      <DireccionClienteSearch
        open={direccionSearchOpen}
        onClose={() => {
          setDireccionSearchOpen(false);
          setDireccionPickerIndex(null);
          setDireccionPickerForComposer(false);
        }}
        nroCliente={
          direccionPickerForComposer
            ? Number(pendingVisitRow?.nroCliente) || 0
            : direccionPickerIndex !== null
              ? Number(activeRows[direccionPickerIndex]?.nroCliente) || 0
              : 0
        }
        onSelect={(d) => {
          if (direccionPickerForComposer) {
            handleDireccionSelected(d, patchPendingVisitRow);
            setDireccionSearchOpen(false);
            setDireccionPickerForComposer(false);
            return;
          }
          if (direccionPickerIndex === null) return;
          handleDireccionSelected(d, (patch) => updateVisitRow(direccionPickerIndex, patch));
          setDireccionSearchOpen(false);
          setDireccionPickerIndex(null);
        }}
      />

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Vista previa — Autocompletar</DialogTitle>
        <DialogContent dividers>
          {!previewData ? (
            <Typography>Sin datos.</Typography>
          ) : (
            <Stack spacing={1.5}>
              <Typography variant="body2">
                Días en el rango: <strong>{previewData.totalDias}</strong>
              </Typography>
              <Typography variant="body2">
                Visitas nuevas estimadas: <strong>{previewData.totalVisitasNuevas}</strong>
              </Typography>
              <Alert severity="info" sx={{ mt: 1 }}>
                Solo clientes asignados al vendedor. Un plan por día; los existentes se completan sin
                borrar visitas.
              </Alert>
              {previewData.porDia.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell align="right">Nuevas</TableCell>
                      <TableCell>Plan</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.porDia.map((d) => (
                      <TableRow key={d.fecha}>
                        <TableCell>{d.fecha}</TableCell>
                        <TableCell align="right">{d.visitasNuevas}</TableCell>
                        <TableCell>{d.planExistente ? 'Existente' : 'Nuevo'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : null}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlanFormDialog;
